import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function DriverNote({ assignmentId, childId, placeholder }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post("/driver/note", { text, assignment_id: assignmentId, child_id: childId || "" }); toast.success("Note saved"); setText(""); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 mt-2">
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} className="rounded-lg text-sm" data-testid={`driver-note-text-${childId || assignmentId}`} />
      <Button size="sm" onClick={send} disabled={busy} className="rounded-full bg-navy text-white text-xs shrink-0" data-testid={`driver-note-send-${childId || assignmentId}`}>Add note</Button>
    </div>
  );
}

export default function DriverRoute() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/driver/today").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  const rows = data?.assignments || [];

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="h-16 flex items-center justify-between gap-2 px-4 border-b border-white/10">
        <div className="flex items-center gap-2 shrink-0"><img src={LOGO} className="h-9 w-9 object-contain" alt="logo" /><p className="font-display font-bold text-sm hidden sm:block">DRIVER ROUTE</p></div>
        <div className="flex items-center gap-1 overflow-x-auto [&>button]:shrink-0">
          <Button variant="ghost" size="sm" className="text-white" onClick={() => nav("/staff/profile")} data-testid="driver-goto-profile">My Profile</Button>
          <Button variant="ghost" size="sm" className="text-white" onClick={() => nav("/staff/transport")} data-testid="driver-goto-transport">Transport Board</Button>
          <Button variant="ghost" size="sm" className="text-white" onClick={() => nav("/staff/camp-roster")} data-testid="driver-goto-camp-roster">Camp Roster</Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }} data-testid="driver-logout"><Icons.LogOut className="w-5 h-5" /></Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        <div><h1 className="font-display font-extrabold text-xl">Hi {user?.name}</h1><p className="text-white/50 text-sm">{data?.date} · {rows.length} stop{rows.length === 1 ? "" : "s"} assigned</p></div>

        {rows.length === 0 && <p className="text-white/50 bg-white/5 rounded-xl p-6 text-center" data-testid="driver-empty">No route assigned to you today. Ask your admin to set up your stops on the Assignments screen.</p>}

        {rows.map((r) => (
          <div key={r.id} className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={`driver-stop-${r.id}`}>
            <div className="flex items-center gap-2">
              <Icons.MapPin className="w-5 h-5 text-gold" />
              <p className="font-display font-bold text-lg">{r.location || "Route"}</p>
            </div>
            {r.retriever_name && <p className="text-xs text-turquoise mt-1" data-testid={`driver-retriever-${r.id}`}>Staff retrieving kids: <b>{r.retriever_name}</b></p>}
            {r.notes && <p className="text-xs text-white/60 mt-1">Plan: {r.notes}</p>}
            <div className="mt-3 space-y-2">
              {(r.children || []).map((c) => (
                <div key={c.id} className="bg-white/5 rounded-xl p-3" data-testid={`driver-child-${c.id}`}>
                  <p className="font-semibold text-sm">{c.first_name} {c.last_name} <span className="font-mono text-turquoise text-xs">{c.explorer_id}</span></p>
                  <p className="text-xs text-white/50">{c.school || "—"}{c.dropoff_preference ? ` · drop-off: ${c.dropoff_preference}` : ""}</p>
                  {c.parent_phone && <p className="text-xs text-white/50">Parent: {c.parent_name} · {c.parent_phone}</p>}
                  <DriverNote assignmentId={r.id} childId={c.id} placeholder={`Note about ${c.first_name}…`} />
                </div>
              ))}
              {(r.children || []).length === 0 && <p className="text-white/40 text-sm">No kids listed for this stop.</p>}
            </div>
            <DriverNote assignmentId={r.id} placeholder="General route note…" />
          </div>
        ))}
      </main>
    </div>
  );
}
